<script setup lang="ts">
import { ref } from 'vue'
import { get, post, put } from '@/api'

definePage({
  meta: {
    title: '업로드 드라이버 관리',
    action: 'read',
    subject: 'Admin',
  },
})

// 업로드 드라이버 인터페이스
interface UploadDriver {
  id: number | null
  driver_name: string | null
  driver_code: string | null
  driver_state: string | null
}

// 초기 드라이버 모델
const getInitialDriver = (): UploadDriver => ({
  id: null,
  driver_name: null,
  driver_code: null,
  driver_state: null,
})

// 상태
const selectedDriver = ref<UploadDriver>(getInitialDriver())
const drivers = ref<UploadDriver[]>([])
const loading = ref(false)
const saving = ref(false)

// 스낵바
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 드라이버 상태 옵션
const driverStateOptions = [
  { value: null, title: '개발중' },
  { value: 'active', title: '사용가능' },
  { value: 'inactive', title: '사용불가능' },
]

// 테이블 헤더
const headers = [
  { title: '드라이버 이름', key: 'driver_name' },
  { title: '드라이버 코드', key: 'driver_code' },
  { title: '드라이버 상태', key: 'driver_state' },
]

// 상태 라벨 반환
const getStateLabel = (state: string | null) => {
  const option = driverStateOptions.find(opt => opt.value === state)

  return option?.title || '개발중'
}

// 상태 색상 반환
const getStateColor = (state: string | null) => {
  switch (state) {
    case 'active':
      return 'success'
    case 'inactive':
      return 'error'
    default:
      return 'warning'
  }
}

// 드라이버 목록 로드
const loadDrivers = async () => {
  loading.value = true
  try {
    const { success, data } = await get<UploadDriver[] | { data: UploadDriver[] }>('/review-upload-driver')
    if (success && data) {
      // 배열 또는 {data: [...]} 형식 모두 처리
      drivers.value = Array.isArray(data) ? data : (data.data || [])
    }
  }
  catch (error) {
    console.error('Failed to load drivers:', error)
    showNotification('드라이버 목록을 불러오는데 실패했습니다.', 'error')
  }
  finally {
    loading.value = false
  }
}

// 드라이버 선택
const selectDriver = (driver: UploadDriver) => {
  selectedDriver.value = JSON.parse(JSON.stringify(driver))
}

// 드라이버 저장
const saveDriver = async () => {
  if (!selectedDriver.value.driver_name) {
    showNotification('드라이버 이름을 입력해주세요.', 'warning')

    return
  }

  if (!selectedDriver.value.id && !selectedDriver.value.driver_code) {
    showNotification('드라이버 코드를 입력해주세요.', 'warning')

    return
  }

  saving.value = true
  try {
    const isUpdate = selectedDriver.value.id !== null

    const url = isUpdate
      ? `/review-upload-driver/${selectedDriver.value.id}`
      : '/review-upload-driver'

    const { success } = isUpdate
      ? await put(url, selectedDriver.value)
      : await post(url, selectedDriver.value)

    if (success) {
      showNotification(isUpdate ? '드라이버가 수정되었습니다.' : '드라이버가 등록되었습니다.')
      await loadDrivers()
      if (!isUpdate)
        resetForm()
    }
    else {
      showNotification('저장에 실패했습니다.', 'error')
    }
  }
  catch (error) {
    console.error('Failed to save driver:', error)
    showNotification('저장에 실패했습니다.', 'error')
  }
  finally {
    saving.value = false
  }
}

// 폼 초기화
const resetForm = () => {
  selectedDriver.value = getInitialDriver()
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 초기 로드
onMounted(() => {
  loadDrivers()
})
</script>

<template>
  <VRow>
    <!-- 드라이버 등록/수정 폼 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>업로드 드라이버 등록 및 수정</VCardTitle>
        <VCardText>
          <div class="text-h6 mb-4">
            드라이버 정보
          </div>

          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedDriver.driver_name"
                label="드라이버 이름"
                placeholder="드라이버 이름을 입력하세요"
                required
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="selectedDriver.driver_code"
                label="드라이버 코드"
                placeholder="드라이버 코드를 입력하세요"
                :disabled="selectedDriver.id !== null"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol cols="12">
              <VRadioGroup
                v-model="selectedDriver.driver_state"
                label="드라이버 상태"
                inline
              >
                <VRadio
                  v-for="option in driverStateOptions"
                  :key="option.value || 'null'"
                  :label="option.title"
                  :value="option.value"
                />
              </VRadioGroup>
            </VCol>
          </VRow>

          <VRow class="mt-4">
            <VCol>
              <VBtn
                v-if="selectedDriver.id"
                variant="outlined"
                class="me-2"
                @click="resetForm"
              >
                초기화
              </VBtn>
              <VBtn
                color="primary"
                :loading="saving"
                @click="saveDriver"
              >
                {{ selectedDriver.id ? '수정하기' : '저장하기' }}
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 드라이버 목록 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center justify-space-between">
          <span>업로드 드라이버 목록</span>
          <VBtn
            icon="tabler-refresh"
            variant="text"
            :loading="loading"
            @click="loadDrivers"
          />
        </VCardTitle>
        <VCardText>
          <VDataTable
            :headers="headers"
            :items="drivers"
            :loading="loading"
            hover
            density="compact"
            class="cursor-pointer"
            @click:row="(_event: Event, { item }: { item: UploadDriver }) => selectDriver(item)"
          >
            <template #item.driver_name="{ item }">
              {{ item.driver_name || '-' }}
            </template>

            <template #item.driver_code="{ item }">
              <code>{{ item.driver_code || '-' }}</code>
            </template>

            <template #item.driver_state="{ item }">
              <VChip
                :color="getStateColor(item.driver_state)"
                size="small"
              >
                {{ getStateLabel(item.driver_state) }}
              </VChip>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
.cursor-pointer :deep(tbody tr) {
  cursor: pointer;
}
</style>
